/**
 * Derived document indexes (W1-03 core-model-ops; ARCHITECTURE.md §5.1, contract `DocIndexes`).
 *
 * Maintained incrementally by `apply` (core/doc/apply.ts) and never persisted:
 *  - notes of a line sorted by (t, x, id); keys of a track sorted by (t, id);
 *  - tracks by owner and tracks of a line grouped by property (canonical property order, layers ascending);
 *  - children of a line (null = root lines) in export order (lines missing from `lineOrder` last, by id);
 *  - export index of a line; BPM points sorted by (t, id); link group members (ids ascending);
 *  - asset users (lines by texture, notes by hitsound, effects by shader, videos, meta references).
 *
 * Storage is a stack of copy-on-write layers (`CowStore`): the container owns the base layer, and a TxDraft puts an
 * overlay layer on top that copies a value the first time it changes it, so draft reads see pending writes while
 * the container stays untouched. Values handed to callers are never mutated afterwards (a later change copies
 * first), so callers may keep them and compare them by identity.
 */

import { Beats } from '../time/beat';
import { propRank } from '../model/props';
import type {
  AssetId,
  AssetUsage,
  BpmId,
  BpmPoint,
  ChartDoc,
  ChartMeta,
  DocIndexes,
  Effect,
  EffectId,
  Key,
  KeyId,
  Line,
  LineId,
  LineProp,
  Note,
  NoteId,
  OrderListName,
  SingletonName,
  SingletonRecords,
  TableName,
  TableRecords,
  Track,
  TrackId,
  TrackOwner,
  Video,
  VideoId,
} from '../model/types';

// ---------------------------------------------------------------------------------------------------------------
// Copy-on-write keyed store
// ---------------------------------------------------------------------------------------------------------------

/** Sorts `arr` in place unless it is sorted already (the comparator is called n - 1 times then). */
function sortIfNeeded<T>(arr: T[], cmp: (a: T, b: T) => number): void {
  for (let i = 1; i < arr.length; i++) {
    if (cmp(arr[i - 1] as T, arr[i] as T) > 0) {
      arr.sort(cmp);
      return;
    }
  }
}

const GONE: unique symbol = Symbol('gone');
type Gone = typeof GONE;

/**
 * Keyed store of index values with an optional base layer. A layer mutates a value in place only while it created
 * it and never handed it out (`hot`); otherwise `edit` copies first. The base layer is never written through.
 */
export class CowStore<K, V> {
  private readonly own = new Map<K, V | Gone>();
  private readonly hot = new Set<K>();
  private readonly base: CowStore<K, V> | null;
  private readonly copy: (v: V) => V;

  constructor(base: CowStore<K, V> | null, copy: (v: V) => V) {
    this.base = base;
    this.copy = copy;
  }

  /** Internal read (the value may change later). */
  peek(k: K): V | undefined {
    const v = this.own.get(k);
    if (v !== undefined) return v === GONE ? undefined : v;
    return this.base?.peek(k);
  }

  /** Read for an external caller: the returned value is frozen in place from now on. */
  publish(k: K): V | undefined {
    const v = this.own.get(k);
    if (v !== undefined) {
      if (v === GONE) return undefined;
      this.hot.delete(k);
      return v;
    }
    return this.base?.publish(k);
  }

  /** A value of this layer that may be mutated in place; created with `make` when absent. */
  edit(k: K, make: () => V): V {
    const v = this.own.get(k);
    if (v !== undefined && v !== GONE) {
      if (this.hot.has(k)) return v;
      const c = this.copy(v);
      this.own.set(k, c);
      this.hot.add(k);
      return c;
    }
    const b = v === GONE ? undefined : this.base?.peek(k);
    const c = b === undefined ? make() : this.copy(b);
    this.own.set(k, c);
    this.hot.add(k);
    return c;
  }

  remove(k: K): void {
    this.hot.delete(k);
    if (this.base !== null && this.base.peek(k) !== undefined) this.own.set(k, GONE);
    else this.own.delete(k);
  }

  forEach(fn: (v: V, k: K) => void): void {
    if (this.base !== null) {
      this.base.forEach((v, k) => {
        if (!this.own.has(k)) fn(v, k);
      });
    }
    for (const [k, v] of this.own) if (v !== GONE) fn(v, k);
  }

  keys(): K[] {
    const out: K[] = [];
    this.forEach((_v, k) => out.push(k));
    return out;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

const EMPTY_IDS: readonly never[] = Object.freeze([]);
const EMPTY_TRACK_MAP: ReadonlyMap<LineProp, readonly TrackId[]> = new Map();
const EMPTY_USAGE: AssetUsage = Object.freeze({
  lines: EMPTY_IDS,
  notes: EMPTY_IDS,
  effects: EMPTY_IDS,
  videos: EMPTY_IDS,
  meta: EMPTY_IDS,
});

type MetaAssetField = 'music' | 'illustration' | 'unlockVideo';
const META_ASSET_FIELDS: readonly MetaAssetField[] = ['music', 'illustration', 'unlockVideo'];

interface Usage {
  lines: LineId[];
  notes: NoteId[];
  effects: EffectId[];
  videos: VideoId[];
  meta: MetaAssetField[];
}

function copyArray<T>(a: T[]): T[] {
  return a.slice();
}

function copyTrackMap(m: Map<LineProp, TrackId[]>): Map<LineProp, TrackId[]> {
  const out = new Map<LineProp, TrackId[]>();
  for (const [k, v] of m) out.set(k, v.slice());
  return out;
}

function copyUsage(u: Usage): Usage {
  return {
    lines: u.lines.slice(),
    notes: u.notes.slice(),
    effects: u.effects.slice(),
    videos: u.videos.slice(),
    meta: u.meta.slice(),
  };
}

function newUsage(): Usage {
  return { lines: [], notes: [], effects: [], videos: [], meta: [] };
}

function usageEmpty(u: Usage): boolean {
  return u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length === 0;
}

/** Map key of a track owner (one track per owner). */
export function ownerKey(owner: TrackOwner): string {
  switch (owner.kind) {
    case 'line':
      return lineOwnerKey(owner.line, owner.prop, owner.layer);
    case 'effectVar':
      return `e:${String(owner.effect)}:${owner.name}`;
    case 'videoVar':
      return `v:${String(owner.video)}:${owner.which}`;
  }
}

export function lineOwnerKey(line: LineId, prop: LineProp, layer: number): string {
  return `l:${String(line)}:${prop}:${String(layer)}`;
}

export function compareNotes(a: Note, b: Note): number {
  const c = Beats.cmp(a.t, b.t);
  if (c !== 0) return c;
  if (a.x < b.x) return -1;
  if (a.x > b.x) return 1;
  return a.id - b.id;
}

export function compareKeys(
  a: { readonly t: Key['t']; readonly id: number },
  b: { readonly t: Key['t']; readonly id: number },
): number {
  const c = Beats.cmp(a.t, b.t);
  return c !== 0 ? c : a.id - b.id;
}

function compareIds(a: number, b: number): number {
  return a - b;
}

/** Position of `id` in `arr` (sorted by `cmp` against `target`, the record of `id`), or -1. */
function findSorted<T extends number>(arr: readonly T[], id: T, cmpToTarget: (el: T) => number): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    const el = arr[mid] as T;
    if (el === id) return mid;
    if (cmpToTarget(el) < 0) lo = mid + 1;
    else hi = mid;
  }
  if (lo < arr.length && arr[lo] === id) return lo;
  // Defensive fallback (an element's sort key changed without passing through the index).
  return arr.indexOf(id);
}

function insertSorted<T extends number>(arr: T[], id: T, cmpToTarget: (el: T) => number): void {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (cmpToTarget(arr[mid] as T) < 0) lo = mid + 1;
    else hi = mid;
  }
  arr.splice(lo, 0, id);
}

function insertId<T extends number>(arr: T[], id: T): void {
  insertSorted(arr, id, (el) => el - id);
}

function removeId<T extends number>(arr: T[], id: T): void {
  const i = findSorted(arr, id, (el) => el - id);
  if (i >= 0) arr.splice(i, 1);
}

// ---------------------------------------------------------------------------------------------------------------
// The index store
// ---------------------------------------------------------------------------------------------------------------

/**
 * Index structures over a document view. `doc` is the live view the indexes describe (the container's state or a
 * draft overlay); it is read only for sort keys of records other than the one being added or removed.
 */
export class DocIndexStore implements DocIndexes {
  private readonly doc: ChartDoc;
  private readonly baseStore: DocIndexStore | null;
  private readonly notes: CowStore<LineId, NoteId[]>;
  private readonly keys: CowStore<TrackId, KeyId[]>;
  private readonly owners: CowStore<string, TrackId[]>;
  private readonly lineTracks: CowStore<LineId, Map<LineProp, TrackId[]>>;
  private readonly children: CowStore<LineId | null, LineId[]>;
  private readonly links: CowStore<number, KeyId[]>;
  private readonly usage: CowStore<AssetId, Usage>;
  private readonly bpm: CowStore<0, BpmId[]>;
  /** Export index by line; null = inherited from the base layer. */
  private lineIdx: Map<LineId, number> | null;
  /** Bulk mode (from-scratch build): lists are appended unsorted and sorted once by `finishBulk`. */
  private bulk = false;

  constructor(doc: ChartDoc, base: DocIndexStore | null) {
    this.doc = doc;
    this.baseStore = base;
    this.notes = new CowStore(base?.notes ?? null, copyArray);
    this.keys = new CowStore(base?.keys ?? null, copyArray);
    this.owners = new CowStore(base?.owners ?? null, copyArray);
    this.lineTracks = new CowStore(base?.lineTracks ?? null, copyTrackMap);
    this.children = new CowStore(base?.children ?? null, copyArray);
    this.links = new CowStore(base?.links ?? null, copyArray);
    this.usage = new CowStore(base?.usage ?? null, copyUsage);
    this.bpm = new CowStore(base?.bpm ?? null, copyArray);
    this.lineIdx = base === null ? new Map() : null;
  }

  /** Builds every index of `doc` from scratch. */
  static build(doc: ChartDoc): DocIndexStore {
    const s = new DocIndexStore(doc, null);
    s.bulk = true;
    s.rebuildLineIndex(doc.lineOrder);
    for (const rec of doc.bpm.values()) s.added('bpm', rec);
    for (const rec of doc.lines.values()) s.added('lines', rec);
    for (const rec of doc.tracks.values()) s.added('tracks', rec);
    for (const rec of doc.keys.values()) s.added('keys', rec);
    for (const rec of doc.notes.values()) s.added('notes', rec);
    for (const rec of doc.effects.values()) s.added('effects', rec);
    for (const rec of doc.videos.values()) s.added('videos', rec);
    s.singletonChanged('meta', null, doc.meta);
    s.finishBulk();
    return s;
  }

  /** A layer over this store for a draft whose view is `doc`. */
  overlay(doc: ChartDoc): DocIndexStore {
    return new DocIndexStore(doc, this);
  }

  private finishBulk(): void {
    this.bulk = false;
    const notes = this.doc.notes;
    const keys = this.doc.keys;
    const bpm = this.doc.bpm;
    // Documents usually list notes and keys in time order already: one linear check instead of a sort (W3-03).
    this.notes.forEach((arr) => {
      sortIfNeeded(arr, (a, b) => compareNotes(notes.get(a) as Note, notes.get(b) as Note));
    });
    this.keys.forEach((arr) => {
      sortIfNeeded(arr, (a, b) => compareKeys(keys.get(a) as Key, keys.get(b) as Key));
    });
    this.bpm.forEach((arr) => arr.sort((a, b) => compareKeys(bpm.get(a) as BpmPoint, bpm.get(b) as BpmPoint)));
    this.owners.forEach((arr) => arr.sort(compareIds));
    this.links.forEach((arr) => arr.sort(compareIds));
    this.lineTracks.forEach((m) => {
      for (const arr of m.values()) arr.sort((a, b) => this.compareTracksOfProp(a, b));
    });
    this.children.forEach((arr) => arr.sort((a, b) => this.compareLines(a, b)));
    this.usage.forEach((u) => {
      u.lines.sort(compareIds);
      u.notes.sort(compareIds);
      u.effects.sort(compareIds);
      u.videos.sort(compareIds);
    });
  }

  // -------------------------------------------------------------------------------------------------------------
  // DocIndexes (arrow properties: safe to pass around)
  // -------------------------------------------------------------------------------------------------------------

  readonly notesByLine = (line: LineId): readonly NoteId[] => this.notes.publish(line) ?? EMPTY_IDS;

  readonly keysByTrack = (track: TrackId): readonly KeyId[] => this.keys.publish(track) ?? EMPTY_IDS;

  readonly track = (line: LineId, prop: LineProp, layer = 0): TrackId | undefined =>
    this.owners.peek(lineOwnerKey(line, prop, layer))?.[0];

  readonly tracksOfLine = (line: LineId): ReadonlyMap<LineProp, readonly TrackId[]> =>
    this.lineTracks.publish(line) ?? EMPTY_TRACK_MAP;

  readonly trackOfOwner = (owner: TrackOwner): TrackId | undefined => this.owners.peek(ownerKey(owner))?.[0];

  readonly childrenOf = (line: LineId | null): readonly LineId[] => this.children.publish(line) ?? EMPTY_IDS;

  readonly lineIndex = (line: LineId): number => this.lineIndexMap().get(line) ?? -1;

  readonly bpmSorted = (): readonly BpmId[] => this.bpm.publish(0) ?? EMPTY_IDS;

  readonly linkMembers = (link: number): readonly KeyId[] =>
    link === 0 ? EMPTY_IDS : (this.links.publish(link) ?? EMPTY_IDS);

  readonly assetUsers = (asset: AssetId): AssetUsage => this.usage.publish(asset) ?? EMPTY_USAGE;

  readonly prevKey = (key: KeyId): KeyId | undefined => this.neighbourKey(key, -1);

  readonly nextKey = (key: KeyId): KeyId | undefined => this.neighbourKey(key, 1);

  private neighbourKey(key: KeyId, dir: -1 | 1): KeyId | undefined {
    const rec = this.doc.keys.get(key);
    if (rec === undefined) return undefined;
    const arr = this.keys.peek(rec.track);
    if (arr === undefined) return undefined;
    const i = findSorted(arr, key, (el) => this.compareKeyTo(el, rec));
    return i < 0 ? undefined : arr[i + dir];
  }

  // -------------------------------------------------------------------------------------------------------------
  // Maintenance hooks (called by apply AFTER the table changed)
  // -------------------------------------------------------------------------------------------------------------

  /** A record was inserted. */
  added<T extends TableName>(tbl: T, rec: TableRecords[T]): void {
    switch (tbl) {
      case 'notes':
        this.addNote(rec as Note);
        break;
      case 'keys':
        this.addKey(rec as Key);
        break;
      case 'tracks':
        this.addTrack(rec as Track);
        break;
      case 'lines':
        this.addLine(rec as Line);
        break;
      case 'bpm':
        this.addBpm(rec as BpmPoint);
        break;
      case 'effects': {
        const e = rec as Effect;
        if (e.shader.kind === 'asset') this.addUse(e.shader.asset, 'effects', e.id);
        break;
      }
      case 'videos': {
        const v = rec as Video;
        this.addUse(v.asset, 'videos', v.id);
        break;
      }
      default:
        break;
    }
  }

  /** A record was deleted (`rec` = the record as it was). */
  removed<T extends TableName>(tbl: T, rec: TableRecords[T]): void {
    switch (tbl) {
      case 'notes':
        this.dropNote(rec as Note);
        break;
      case 'keys':
        this.dropKey(rec as Key);
        break;
      case 'tracks':
        this.dropTrack(rec as Track);
        break;
      case 'lines':
        this.dropLine(rec as Line);
        break;
      case 'bpm':
        this.dropBpm(rec as BpmPoint);
        break;
      case 'effects': {
        const e = rec as Effect;
        if (e.shader.kind === 'asset') this.dropUse(e.shader.asset, 'effects', e.id);
        break;
      }
      case 'videos': {
        const v = rec as Video;
        this.dropUse(v.asset, 'videos', v.id);
        break;
      }
      default:
        break;
    }
  }

  /** A record was replaced (`before` -> `after`, same id). */
  changed<T extends TableName>(tbl: T, before: TableRecords[T], after: TableRecords[T]): void {
    switch (tbl) {
      case 'notes': {
        const a = before as Note;
        const b = after as Note;
        if (a.line !== b.line || a.x !== b.x || !Beats.eq(a.t, b.t) || !sameHitsoundAsset(a, b)) {
          this.dropNote(a);
          this.addNote(b);
        }
        break;
      }
      case 'keys': {
        const a = before as Key;
        const b = after as Key;
        if (a.track !== b.track || a.link !== b.link || !Beats.eq(a.t, b.t)) {
          this.dropKey(a);
          this.addKey(b);
        }
        break;
      }
      case 'tracks': {
        const a = before as Track;
        const b = after as Track;
        if (ownerKey(a.owner) !== ownerKey(b.owner)) {
          this.dropTrack(a);
          this.addTrack(b);
        }
        break;
      }
      case 'lines': {
        const a = before as Line;
        const b = after as Line;
        if (a.parent !== b.parent || a.texture !== b.texture) {
          this.dropLine(a);
          this.addLine(b);
        }
        break;
      }
      case 'bpm': {
        const a = before as BpmPoint;
        const b = after as BpmPoint;
        if (!Beats.eq(a.t, b.t)) {
          this.dropBpm(a);
          this.addBpm(b);
        }
        break;
      }
      case 'effects': {
        const a = before as Effect;
        const b = after as Effect;
        const sa = a.shader.kind === 'asset' ? a.shader.asset : null;
        const sb = b.shader.kind === 'asset' ? b.shader.asset : null;
        if (sa !== sb) {
          if (sa !== null) this.dropUse(sa, 'effects', a.id);
          if (sb !== null) this.addUse(sb, 'effects', b.id);
        }
        break;
      }
      case 'videos': {
        const a = before as Video;
        const b = after as Video;
        if (a.asset !== b.asset) {
          this.dropUse(a.asset, 'videos', a.id);
          this.addUse(b.asset, 'videos', b.id);
        }
        break;
      }
      default:
        break;
    }
  }

  /** A singleton was replaced (only `meta` carries indexed references). `before` null = initial build. */
  singletonChanged<S extends SingletonName>(
    key: S,
    before: SingletonRecords[S] | null,
    after: SingletonRecords[S],
  ): void {
    if (key !== 'meta') return;
    const a = before as ChartMeta | null;
    const b = after as ChartMeta;
    for (const field of META_ASSET_FIELDS) {
      const was = a === null ? null : a[field];
      const now = b[field];
      if (was === now) continue;
      if (was !== null) this.dropMetaUse(was, field);
      if (now !== null) this.addMetaUse(now, field);
    }
  }

  /** An order list was replaced. */
  orderChanged(list: OrderListName, next: readonly number[]): void {
    if (list !== 'lineOrder') return;
    this.rebuildLineIndex(next as readonly LineId[]);
    for (const parent of this.children.keys()) {
      this.children.edit(parent, () => []).sort((a, b) => this.compareLines(a, b));
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Per-table maintenance
  // -------------------------------------------------------------------------------------------------------------

  private addNote(n: Note): void {
    const arr = this.notes.edit(n.line, () => []);
    if (this.bulk) arr.push(n.id);
    else insertSorted(arr, n.id, (el) => this.compareNoteTo(el, n));
    if (n.hitsound?.kind === 'asset') this.addUse(n.hitsound.asset, 'notes', n.id);
  }

  private dropNote(n: Note): void {
    const cur = this.notes.peek(n.line);
    if (cur !== undefined) {
      const i = findSorted(cur, n.id, (el) => this.compareNoteTo(el, n));
      if (i >= 0) {
        if (cur.length === 1) this.notes.remove(n.line);
        else this.notes.edit(n.line, () => []).splice(i, 1);
      }
    }
    if (n.hitsound?.kind === 'asset') this.dropUse(n.hitsound.asset, 'notes', n.id);
  }

  private addKey(k: Key): void {
    const arr = this.keys.edit(k.track, () => []);
    if (this.bulk) arr.push(k.id);
    else insertSorted(arr, k.id, (el) => this.compareKeyTo(el, k));
    if (k.link !== 0) {
      const l = this.links.edit(k.link, () => []);
      if (this.bulk) l.push(k.id);
      else insertId(l, k.id);
    }
  }

  private dropKey(k: Key): void {
    const cur = this.keys.peek(k.track);
    if (cur !== undefined) {
      const i = findSorted(cur, k.id, (el) => this.compareKeyTo(el, k));
      if (i >= 0) {
        if (cur.length === 1) this.keys.remove(k.track);
        else this.keys.edit(k.track, () => []).splice(i, 1);
      }
    }
    if (k.link !== 0) this.dropFromIdList(this.links, k.link, k.id);
  }

  private addTrack(t: Track): void {
    const ok = ownerKey(t.owner);
    const arr = this.owners.edit(ok, () => []);
    if (this.bulk) arr.push(t.id);
    else insertId(arr, t.id);
    if (t.owner.kind === 'line') {
      const { line, prop } = t.owner;
      const m = this.lineTracks.edit(line, () => new Map());
      let ids = m.get(prop);
      if (ids === undefined) {
        ids = [];
        m.set(prop, ids);
        if (m.size > 1) {
          // Keep the iteration order canonical (property order of LINE_PROPS).
          const entries = [...m.entries()].sort((a, b) => propRank(a[0]) - propRank(b[0]));
          m.clear();
          for (const [p, list] of entries) m.set(p, list);
        }
      }
      if (this.bulk) ids.push(t.id);
      else insertSorted(ids, t.id, (el) => this.compareTracksOfProp(el, t.id, t));
    }
  }

  private dropTrack(t: Track): void {
    this.dropFromIdList(this.owners, ownerKey(t.owner), t.id);
    if (t.owner.kind === 'line') {
      const { line, prop } = t.owner;
      const cur = this.lineTracks.peek(line);
      const ids = cur?.get(prop);
      if (cur === undefined || ids === undefined || !ids.includes(t.id)) return;
      if (ids.length === 1 && cur.size === 1) {
        this.lineTracks.remove(line);
        return;
      }
      const m = this.lineTracks.edit(line, () => new Map());
      const own = m.get(prop) ?? [];
      const i = own.indexOf(t.id);
      if (i >= 0) own.splice(i, 1);
      if (own.length === 0) m.delete(prop);
    }
  }

  private addLine(l: Line): void {
    const arr = this.children.edit(l.parent, () => []);
    if (this.bulk) arr.push(l.id);
    else insertSorted(arr, l.id, (el) => this.compareLines(el, l.id));
    if (l.texture !== null) this.addUse(l.texture, 'lines', l.id);
  }

  private dropLine(l: Line): void {
    const cur = this.children.peek(l.parent);
    if (cur !== undefined) {
      const i = cur.indexOf(l.id);
      if (i >= 0) {
        if (cur.length === 1) this.children.remove(l.parent);
        else this.children.edit(l.parent, () => []).splice(i, 1);
      }
    }
    if (l.texture !== null) this.dropUse(l.texture, 'lines', l.id);
  }

  private addBpm(b: BpmPoint): void {
    const arr = this.bpm.edit(0, () => []);
    if (this.bulk) arr.push(b.id);
    else insertSorted(arr, b.id, (el) => this.compareBpmTo(el, b));
  }

  private dropBpm(b: BpmPoint): void {
    const cur = this.bpm.peek(0);
    if (cur === undefined) return;
    const i = findSorted(cur, b.id, (el) => this.compareBpmTo(el, b));
    if (i < 0) return;
    if (cur.length === 1) this.bpm.remove(0);
    else this.bpm.edit(0, () => []).splice(i, 1);
  }

  private dropFromIdList<K, T extends number>(store: CowStore<K, T[]>, k: K, id: T): void {
    const cur = store.peek(k);
    if (cur === undefined || !cur.includes(id)) return;
    if (cur.length === 1) store.remove(k);
    else
      removeId(
        store.edit(k, () => []),
        id,
      );
  }

  private addUse(asset: AssetId, field: 'lines' | 'notes' | 'effects' | 'videos', id: number): void {
    const u = this.usage.edit(asset, newUsage);
    const list = u[field] as number[];
    if (this.bulk) list.push(id);
    else insertId(list, id);
  }

  private dropUse(asset: AssetId, field: 'lines' | 'notes' | 'effects' | 'videos', id: number): void {
    const cur = this.usage.peek(asset);
    if (cur === undefined || !(cur[field] as number[]).includes(id)) return;
    const u = this.usage.edit(asset, newUsage);
    removeId(u[field] as number[], id);
    if (usageEmpty(u)) this.usage.remove(asset);
  }

  private addMetaUse(asset: AssetId, field: MetaAssetField): void {
    const u = this.usage.edit(asset, newUsage);
    if (!u.meta.includes(field)) {
      u.meta.push(field);
      u.meta.sort((a, b) => META_ASSET_FIELDS.indexOf(a) - META_ASSET_FIELDS.indexOf(b));
    }
  }

  private dropMetaUse(asset: AssetId, field: MetaAssetField): void {
    const cur = this.usage.peek(asset);
    if (cur?.meta.includes(field) !== true) return;
    const u = this.usage.edit(asset, newUsage);
    u.meta.splice(u.meta.indexOf(field), 1);
    if (usageEmpty(u)) this.usage.remove(asset);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Ordering
  // -------------------------------------------------------------------------------------------------------------

  private lineIndexMap(): ReadonlyMap<LineId, number> {
    if (this.lineIdx !== null) return this.lineIdx;
    return (this.baseStore as DocIndexStore).lineIndexMap();
  }

  private rebuildLineIndex(order: readonly LineId[]): void {
    const m = new Map<LineId, number>();
    order.forEach((id, i) => {
      if (!m.has(id)) m.set(id, i);
    });
    this.lineIdx = m;
  }

  private compareLines(a: LineId, b: LineId): number {
    const m = this.lineIndexMap();
    const ia = m.get(a) ?? Number.MAX_SAFE_INTEGER;
    const ib = m.get(b) ?? Number.MAX_SAFE_INTEGER;
    return ia !== ib ? ia - ib : a - b;
  }

  private compareNoteTo(el: NoteId, target: Note): number {
    if (el === target.id) return 0;
    const rec = this.doc.notes.get(el);
    return rec === undefined ? el - target.id : compareNotes(rec, target);
  }

  private compareKeyTo(el: KeyId, target: Key): number {
    if (el === target.id) return 0;
    const rec = this.doc.keys.get(el);
    return rec === undefined ? el - target.id : compareKeys(rec, target);
  }

  private compareBpmTo(el: BpmId, target: BpmPoint): number {
    if (el === target.id) return 0;
    const rec = this.doc.bpm.get(el);
    return rec === undefined ? el - target.id : compareKeys(rec, target);
  }

  /** Tracks of one (line, prop): by layer, then id. */
  private compareTracksOfProp(a: TrackId, b: TrackId, bRec?: Track): number {
    if (a === b) return 0;
    const ta = this.doc.tracks.get(a);
    const tb = bRec ?? this.doc.tracks.get(b);
    const la = ta?.owner.kind === 'line' ? ta.owner.layer : 0;
    const lb = tb?.owner.kind === 'line' ? tb.owner.layer : 0;
    return la !== lb ? la - lb : a - b;
  }
}

function sameHitsoundAsset(a: Note, b: Note): boolean {
  const x = a.hitsound?.kind === 'asset' ? a.hitsound.asset : null;
  const y = b.hitsound?.kind === 'asset' ? b.hitsound.asset : null;
  return x === y;
}

/** Asset ids referenced by the meta singleton (for asset-user checks). */
export function metaAssetRefs(meta: ChartMeta): ReadonlyArray<readonly [MetaAssetField, AssetId]> {
  const out: Array<readonly [MetaAssetField, AssetId]> = [];
  for (const f of META_ASSET_FIELDS) {
    const id = meta[f];
    if (id !== null) out.push([f, id]);
  }
  return out;
}
